import { InfoboxService } from '@/common/infobox/infobox.service';
import { DatePipe, NgClass } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	OnInit,
	signal,
	viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { filter, finalize, forkJoin } from 'rxjs';
import { DisplayFile, NewUploadedFile, ApiUploadedFile } from './term-files-section.model';
import { TermFilesApiService } from './api/term-files.api.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { RemoveFilesConfirmDialogComponent } from './remove-files-confirm-dialog/remove-files-confirm-dialog.component';

@Component({
	selector: 'app-term-files-section',
	standalone: true,
	imports: [
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatCardModule,
		DatePipe,
		MatProgressSpinnerModule,
		MatButtonToggleModule,
		NgClass,
	],
	templateUrl: './term-files-section.component.html',
	styleUrl: './term-files-section.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermFilesSectionComponent implements OnInit {
	readonly termId = input.required<number>();

	readonly loggedUserId = signal<number>(null);
	readonly isLoading = signal(false);
	readonly gridStyle = input<'list' | 'grid'>('list');
	readonly externalUrlToAdd = signal('');

	readonly MAX_SIZE_MB = 5;

	readonly displayFiles = computed<DisplayFile[]>(() => [
		...this.existingFiles().map((f) => ({ ...f, isNew: false }) as DisplayFile),
		...this.newFiles().map((f) => ({ ...f, isNew: true }) as DisplayFile),
	]);

	readonly someFileIsInvalid = computed(() => this.newFiles().some((file) => !file.note) || !this.newFiles().length);

	private readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

	private readonly newFiles = signal<NewUploadedFile[]>([]);
	private readonly existingFiles = signal<ApiUploadedFile[]>([]);

	private readonly infoboxService = inject(InfoboxService);
	private readonly termFilesApiService = inject(TermFilesApiService);
	private readonly matDialog = inject(MatDialog);

	ngOnInit(): void {
		this.isLoading.set(true);
		forkJoin({
			files: this.termFilesApiService.getTransportTermFiles(this.termId()),
			profile: this.termFilesApiService.getProfile(),
		})
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe(({ files, profile }) => {
				this.existingFiles.set(files);

				if (profile) {
					this.loggedUserId.set(profile.id);
				}
			});
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;

		if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
			this.infoboxService.warning(`Maksymalny rozmiar pliku to ${this.MAX_SIZE_MB}MB`);
			return;
		}

		this.newFiles.update((arr) => [...arr, { file, note: '', externalUrl: '' }]);
		this.scrollToBottomSmoothAfterRender();
	}

	addFileWithExternalUrl(): void {
		if (!this.externalUrlToAdd().trim()) {
			return;
		}

		this.newFiles.update((arr) => [...arr, { file: '', note: '', externalUrl: this.externalUrlToAdd() }]);
		this.externalUrlToAdd.set('');
		this.scrollToBottomSmoothAfterRender();
	}

	onNoteChange(file: DisplayFile, note: string): void {
		this.newFiles.update((files) =>
			files.map((f) => {
				const isMatch =
					(file.file instanceof File && f.file === file.file) ||
					(file.externalUrl && f.externalUrl === file.externalUrl);

				return isMatch ? { ...f, note } : f;
			}),
		);
	}

	removeFile(file: DisplayFile): void {
		if (file.isNew) {
			this.removeNewFile(file);
			return;
		}

		this.removeExistingFile(file as ApiUploadedFile);
	}

	save(): void {
		const requests = this.newFiles().map((file) =>
			this.termFilesApiService.createTransportTermFile(this.termId(), file),
		);

		if (requests.length === 0) {
			return;
		}

		this.isLoading.set(true);

		forkJoin(requests)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((savedFiles) => {
				this.infoboxService.success('Pliki zapisane pomyślnie');
				const combined = [...this.existingFiles(), ...savedFiles];
				this.existingFiles.set(combined);
				this.newFiles.set([]);
			});
	}

	private removeNewFile(file: DisplayFile): void {
		this.newFiles.update((files) => files.filter((f) => f.file !== file.file));
	}

	private removeExistingFile(file: ApiUploadedFile): void {
		this.matDialog
			.open(RemoveFilesConfirmDialogComponent)
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => {
				this.isLoading.set(true);

				this.termFilesApiService
					.deleteTransportTermFile(this.termId(), file.id)
					.pipe(finalize(() => this.isLoading.set(false)))
					.subscribe(() => {
						const updated = this.existingFiles().filter((f) => f.id !== file.id);
						this.existingFiles.set(updated);
					});
			});
	}

	private scrollToBottomSmoothAfterRender(): void {
		queueMicrotask(() => {
			requestAnimationFrame(() => {
				const container = this.scrollContainer()?.nativeElement;
				if (container) {
					container.scrollTo({
						top: container.scrollHeight,
						behavior: 'smooth',
					});
				}
			});
		});
	}
}
